/**
 * Video Banner Pro
 * Vanilla JS — sin dependencias externas ni SDKs de terceros.
 *
 * - Navega el carrusel (flechas, dots, teclado).
 * - Reproduce el video solo del slide activo (nativo <video>, YouTube o
 *   Vimeo vía postMessage nativo — sin cargar sus librerías JS oficiales).
 * - Pausa automáticamente cuando la sección sale del viewport
 *   (IntersectionObserver).
 * - Respeta prefers-reduced-motion: no hace autoplay de video en ese caso.
 */

class VideoBannerPro {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('[data-vbp-track]');
    this.slides = Array.from(section.querySelectorAll('[data-vbp-slide]'));
    this.prevButton = section.querySelector('[data-vbp-prev]');
    this.nextButton = section.querySelector('[data-vbp-next]');
    this.playPauseButton = section.querySelector('[data-vbp-playpause]');
    this.dots = Array.from(section.querySelectorAll('[data-vbp-dot]'));
    this.numbersCurrent = section.querySelector('[data-vbp-current]');

    if (!this.track || this.slides.length === 0) return;

    this.currentIndex = 0;
    this.isSectionVisible = true;
    this.isPaused = false;
    this.prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPlayPauseClick = this.onPlayPauseClick.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);

    this.bindEvents();
    this.setupIntersectionObserver();
    this.goToSlide(0, { instant: true });
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onPrevClick);
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onNextClick);
    }
    if (this.playPauseButton) {
      this.playPauseButton.addEventListener('click', this.onPlayPauseClick);
    }

    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

    this.track.addEventListener('keydown', this.onKeydown);
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      this.handleIntersection,
      { threshold: 0.25 }
    );
    this.observer.observe(this.section);
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      this.isSectionVisible = entry.isIntersecting;
      if (this.isSectionVisible && !this.isPaused) {
        this.playActiveVideo();
      } else {
        this.pauseAllVideos();
      }
    });
  }

  /* ---------------- Navigation ---------------- */

  onPrevClick() {
    const newIndex =
      (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(newIndex);
  }

  onNextClick() {
    const newIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(newIndex);
  }

  onDotClick(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    this.goToSlide(index);
  }

  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.onNextClick();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.onPrevClick();
    }
  }

  goToSlide(index, options = {}) {
    this.pauseAllVideos();

    const previousIndex = this.currentIndex;
    this.currentIndex = index;

    const offset = index * -100;
    this.track.style.transition =
      options.instant || this.prefersReducedMotion ? 'none' : '';
    this.track.style.transform = `translateX(${offset}%)`;

    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (this.numbersCurrent) {
      this.numbersCurrent.textContent = index + 1;
    }

    this.isPaused = false;
    this.updatePlayPauseUI();

    if (this.isSectionVisible) {
      this.playActiveVideo();
    }
  }

  /* ---------------- Video control ---------------- */

  getActiveVideoEl() {
    const activeSlide = this.slides[this.currentIndex];
    if (!activeSlide) return null;
    return activeSlide.querySelector('[data-vbp-video]');
  }

  playActiveVideo() {
    if (this.prefersReducedMotion) return;

    const videoEl = this.getActiveVideoEl();
    const activeSlide = this.slides[this.currentIndex];
    if (!videoEl || !activeSlide) return;

    const type = videoEl.dataset.vbpVideo;

    if (type === 'native') {
      const playPromise = videoEl.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(() => {
          /* autoplay bloqueado por el navegador; se queda en la imagen */
        });
      }
      videoEl.addEventListener(
        'playing',
        () => activeSlide.classList.add('is-video-ready'),
        { once: true }
      );
    } else if (type === 'youtube') {
      this.postCommand(videoEl, { event: 'command', func: 'playVideo', args: [] });
      activeSlide.classList.add('is-video-ready');
    } else if (type === 'vimeo') {
      this.postCommand(videoEl, { method: 'play' });
      activeSlide.classList.add('is-video-ready');
    }
  }

  pauseAllVideos() {
    this.slides.forEach((slide) => {
      const videoEl = slide.querySelector('[data-vbp-video]');
      if (!videoEl) return;
      const type = videoEl.dataset.vbpVideo;

      if (type === 'native' && typeof videoEl.pause === 'function') {
        videoEl.pause();
      } else if (type === 'youtube') {
        this.postCommand(videoEl, { event: 'command', func: 'pauseVideo', args: [] });
      } else if (type === 'vimeo') {
        this.postCommand(videoEl, { method: 'pause' });
      }
    });
  }

  postCommand(iframeEl, message) {
    if (!iframeEl || !iframeEl.contentWindow) return;
    try {
      iframeEl.contentWindow.postMessage(JSON.stringify(message), '*');
    } catch (e) {
      /* silenciosamente ignorado: el iframe puede no estar listo aún */
    }
  }

  onPlayPauseClick() {
    this.isPaused = !this.isPaused;

    if (this.isPaused) {
      this.pauseAllVideos();
    } else if (this.isSectionVisible) {
      this.playActiveVideo();
    }

    this.updatePlayPauseUI();
  }

  updatePlayPauseUI() {
    if (!this.playPauseButton) return;
    this.playPauseButton.setAttribute('aria-pressed', this.isPaused ? 'true' : 'false');
    this.playPauseButton.setAttribute(
      'aria-label',
      this.isPaused ? 'Reproducir video' : 'Pausar video'
    );
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
    this.pauseAllVideos();

    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrevClick);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNextClick);
    if (this.playPauseButton) {
      this.playPauseButton.removeEventListener('click', this.onPlayPauseClick);
    }
    this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
    this.track.removeEventListener('keydown', this.onKeydown);
  }
}

function initVideoBannerPro(root = document) {
  const sections = root.querySelectorAll('[data-video-banner-pro]');
  sections.forEach((section) => {
    if (section.videoBannerProInstance) return;
    section.videoBannerProInstance = new VideoBannerPro(section);
  });
}

document.addEventListener('DOMContentLoaded', () => initVideoBannerPro());

document.addEventListener('shopify:section:load', (event) => {
  initVideoBannerPro(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('[data-video-banner-pro]');
  if (section && section.videoBannerProInstance) {
    section.videoBannerProInstance.destroy();
    delete section.videoBannerProInstance;
  }
});